<template>
  <div :class="{ [bem.is('vertical')]: vertical, [bem.b()]: true }">
    <div ref="bar" :class="bem.m('bar')" @click="handleClick" />
    <div
      ref="thumb"
      :class="bem.m('thumb')"
      :style="{ left: thumbLeft + 'px', top: thumbTop + 'px' }"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue'
import { hueSliderProps } from '../config'
import draggable from '../config/draggable'
import { createNamespace } from '@ll-plus/utils'
import type { Nullable } from '@ll-plus/utils'

// Config
defineOptions({ name: 'HueSlider' })
const props = defineProps(hueSliderProps)
const bem = createNamespace('hue-slider')

// Data
const instance = getCurrentInstance()
const thumb = ref<Nullable<HTMLElement>>(null)
const bar = ref<Nullable<HTMLElement>>(null)
const thumbLeft = ref<number>(0)
const thumbTop = ref<number>(0)
const hueValue = computed(() => props.color?.get('hue') ?? false)
// Methods
const handleClick = (event: MouseEvent) => {
  const target = event.target

  if (target !== thumb.value) {
    handleDrag(event)
  }
}
const handleDrag = (event: MouseEvent) => {
  const ele = instance?.vnode.el as HTMLElement
  const rect = ele.getBoundingClientRect()
  let hue

  if (!props.vertical) {
    let left = event.clientX - rect.left
    left = Math.min(
      left,
      rect.width - (thumb.value as HTMLElement).offsetWidth / 2
    )
    left = Math.max((thumb.value as HTMLElement).offsetWidth / 2, left)

    hue = Math.round(
      ((left - (thumb.value as HTMLElement).offsetWidth / 2) /
        (rect.width - (thumb.value as HTMLElement).offsetWidth)) *
        360
    )
  } else {
    let top = event.clientY - rect.top

    top = Math.min(
      top,
      rect.height - (thumb.value as HTMLElement).offsetHeight / 2
    )
    top = Math.max((thumb.value as HTMLElement).offsetHeight / 2, top)
    hue = Math.round(
      ((top - (thumb.value as HTMLElement).offsetHeight / 2) /
        (rect.height - (thumb.value as HTMLElement).offsetHeight)) *
        360
    )
  }
  props.color.set('hue', hue)
}
const getThumbLeft = (): number => {
  const ele = instance?.vnode.el as HTMLElement

  if (props.vertical) return 0
  const hue = props.color.get('hue')

  if (!ele) return 0
  return Math.round(
    (hue * (ele.offsetWidth - (thumb.value as HTMLElement).offsetWidth / 2)) /
      360
  )
}
const getThumbTop = (): number => {
  const ele = instance?.vnode.el as HTMLElement
  if (!props.vertical) return 0
  const hue = props.color.get('hue')

  if (!ele) return 0
  return Math.round(
    (hue * (ele.offsetHeight - (thumb.value as HTMLElement).offsetHeight / 2)) /
      360
  )
}
const update = () => {
  thumbLeft.value = getThumbLeft()
  thumbTop.value = getThumbTop()
}

// watch
watch(
  () => hueValue.value,
  () => {
    update()
  }
)

onMounted(() => {
  const dragConfig = {
    drag: (event: MouseEvent) => {
      handleDrag(event)
    },
    end: (event: MouseEvent) => {
      handleDrag(event)
    }
  }

  draggable(bar.value as HTMLElement, dragConfig)
  draggable(thumb.value as HTMLElement, dragConfig)
  update()
})

defineExpose({
  update
})
</script>
